import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  DOKUMENT_TYP_LABELS,
  REFERENZ_DOKUMENT_TYPEN,
  dokumentDisplayNumber,
  formatReferenzdokumentLabel,
  formatVerknuepfungLine,
  getDokumentListStatusLabel,
  listDokumente,
  replaceDokumentReferenzen,
  selectedReferenzDokumentIds,
  type DokumentDetail,
  type DokumentListItem,
  type DokumentTyp,
} from "@/lib/dokumente";

interface DocumentReferenzenDialogProps {
  open: boolean;
  onClose: () => void;
  doc: DokumentDetail;
  editable: boolean;
  onApplied: (updated: DokumentDetail) => void;
  zIndexClass?: string;
}

function candidateLabel(item: DokumentListItem): string {
  const typ = DOKUMENT_TYP_LABELS[item.dokument_typ] || item.dokument_typ;
  const nummer = dokumentDisplayNumber(item);
  const status = getDokumentListStatusLabel(item);
  return `${nummer} · ${typ}${status ? ` · ${status}` : ""}`;
}

export function DocumentReferenzenDialog({
  open,
  onClose,
  doc,
  editable,
  onApplied,
  zIndexClass,
}: DocumentReferenzenDialogProps) {
  const [candidates, setCandidates] = useState<DokumentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [anzeigen, setAnzeigen] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(selectedReferenzDokumentIds(doc));
    setAnzeigen(doc.referenzdokument_anzeigen !== false);
    setError(null);
  }, [open, doc]);

  useEffect(() => {
    if (!open) return;
    if (!doc.auftrag_id) {
      setCandidates([]);
      setLoading(false);
      return;
    }
    const auftragId = doc.auftrag_id;
    const controller = new AbortController();
    setLoading(true);
    void listDokumente({ auftrag_id: auftragId, limit: 200, sort: "created_at", direction: "desc" })
      .then((rows) => {
        if (controller.signal.aborted) return;
        setCandidates(
          rows.filter(
            (row) =>
              row.id !== doc.id &&
              row.id > 0 &&
              REFERENZ_DOKUMENT_TYPEN.includes(row.dokument_typ as DokumentTyp),
          ),
        );
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(
          err instanceof ApiError
            ? err.message
            : "Dokumente des Projekts konnten nicht geladen werden.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, doc.id, doc.auftrag_id]);

  const grouped = useMemo(() => {
    const byTyp = new Map<DokumentTyp, DokumentListItem[]>();
    for (const typ of REFERENZ_DOKUMENT_TYPEN) {
      const rows = candidates.filter((row) => row.dokument_typ === typ);
      if (rows.length) byTyp.set(typ, rows);
    }
    return byTyp;
  }, [candidates]);

  function toggleId(id: number) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function handleSave() {
    if (!editable) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await replaceDokumentReferenzen(doc.id, {
        referenz_dokument_ids: selectedIds,
        anzeigen,
        version: doc.version,
      });
      onApplied(updated);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Referenzdokumente konnten nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  const currentLabel = formatReferenzdokumentLabel(doc);

  return (
    <Dialog
      open={open}
      onClose={saving ? () => undefined : onClose}
      title="Referenzdokumente"
      description="Wählen Sie, welche Dokumente dieses Projekts als Referenz auf der Rechnung erscheinen sollen – einzeln oder mehrere."
      size="lg"
      zIndexClass={zIndexClass}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          {editable ? (
            <Button type="button" onClick={() => void handleSave()} loading={saving}>
              Übernehmen
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="space-y-4">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={anzeigen}
            onChange={(e) => setAnzeigen(e.target.checked)}
            disabled={!editable}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-label">Referenzdokumente im Dokument anzeigen</span>
            <span className="block text-xs text-label-tertiary">
              Erscheint im Infoblock als „Referenzdokument: …“. Aus: die Verknüpfungen bleiben,
              stehen aber nicht auf dem PDF.
            </span>
          </span>
        </label>

        {currentLabel ? (
          <p className="text-sm text-label-secondary">Aktuell: {currentLabel}</p>
        ) : (
          <p className="text-sm text-label-secondary">
            Noch keine Referenz gewählt. Ohne Auswahl bleibt die Umwandlungsquelle (z. B. das
            Angebot) der Fallback.
          </p>
        )}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        {!doc.auftrag_id ? (
          <p className="text-sm text-label-secondary">
            Dieses Dokument ist keinem Projekt zugeordnet. Weitere Referenzen können deshalb nicht
            ausgewählt werden.
          </p>
        ) : loading ? (
          <div className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner className="h-4 w-4" />
            Dokumente werden geladen …
          </div>
        ) : candidates.length === 0 ? (
          <p className="text-sm text-label-secondary">
            Im Projekt gibt es keine weiteren Angebote, Lieferscheine, Auftragsbestätigungen oder
            Rechnungen.
          </p>
        ) : (
          <div className="max-h-[22rem] space-y-4 overflow-y-auto pr-1">
            {REFERENZ_DOKUMENT_TYPEN.map((typ) => {
              const rows = grouped.get(typ);
              if (!rows?.length) return null;
              return (
                <div key={typ}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
                    {DOKUMENT_TYP_LABELS[typ]}
                  </h3>
                  <ul className="space-y-1.5">
                    {rows.map((row) => (
                      <li key={row.id}>
                        <label className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(row.id)}
                            onChange={() => toggleId(row.id)}
                            disabled={!editable}
                            className="mt-0.5"
                          />
                          <span className="text-label">{candidateLabel(row)}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
}

export function DocumentVerknuepfungenBlock({
  doc,
  editable,
  onEdit,
}: {
  doc: DokumentDetail;
  editable: boolean;
  onEdit: () => void;
}) {
  const links = doc.verknuepfungen || [];
  const referenzLabel = formatReferenzdokumentLabel(doc);
  const empty = links.length === 0 && !referenzLabel;
  if (!editable && empty) return null;

  const interactive = editable;
  return (
    <button
      type="button"
      className={clsx(
        "w-full rounded-xl border border-separator bg-card p-4 text-left transition",
        interactive && "cursor-pointer hover:border-accent/40 hover:bg-card-elevated",
        empty && "border-dashed",
      )}
      onClick={interactive ? onEdit : undefined}
      disabled={!interactive}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-label">Verknüpfungen</h3>
        {interactive ? <span className="text-xs text-accent">Bearbeiten</span> : null}
      </div>
      {links.length ? (
        <ul className="space-y-1 text-sm text-label-secondary">
          {links.map((link) => (
            <li key={link.id}>{formatVerknuepfungLine(link)}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-label-secondary">
          Keine Verknüpfungen. Klicken, um Angebot, Lieferschein oder weitere Dokumente als
          Referenz auszuwählen.
        </p>
      )}
      {referenzLabel && doc.referenzdokument_anzeigen !== false ? (
        <p className="mt-2 text-xs text-label-tertiary">
          Auf dem Dokument: {referenzLabel}
        </p>
      ) : null}
    </button>
  );
}
